import React from 'react';
import { AlertTriangle, AlertCircle, Info, TrendingUp } from 'lucide-react';

const PRIORITY_CONFIG = {
  CRITICAL: { icon: AlertTriangle, color: 'var(--zt-gap)', label: 'Critical' },
  HIGH: { icon: AlertCircle, color: 'var(--zt-partial)', label: 'High' },
  MEDIUM: { icon: Info, color: 'var(--zt-advanced)', label: 'Medium' },
  LOW: { icon: Info, color: 'var(--color-text-muted)', label: 'Low' }
};

const PILLAR_COLORS = {
  IDENTITY: 'var(--zt-identity)',
  DEVICE: 'var(--zt-device)',
  NETWORK: 'var(--zt-network)',
  APPLICATION: 'var(--zt-application)',
  DATA: 'var(--zt-data)',
  VISIBILITY: 'var(--zt-visibility)'
};

const GapAnalysis = ({ gaps, loading }) => {
  if (loading) {
    return (
      <div className="gap-analysis gap-analysis--loading">
        <div className="loading-dots"><span /><span /><span /></div>
      </div>
    );
  }

  if (!gaps) {
    return (
      <div className="gap-analysis gap-analysis--empty">
        <AlertCircle size={32} style={{ color: 'var(--color-text-muted)' }} />
        <p>No gap analysis data available</p>
      </div>
    );
  }

  const { totalGaps = 0, criticalGaps = 0, gapsByPillar = [] } = gaps;

  return (
    <div className="gap-analysis">
      {/* Summary stats */}
      <div className="gap-summary-stats">
        <div className="gap-stat gap-stat--total">
          <span className="gap-stat-value">{totalGaps}</span>
          <span className="gap-stat-label">Total Gaps</span>
        </div>
        <div className="gap-stat gap-stat--critical">
          <AlertTriangle size={16} style={{ color: 'var(--zt-gap)' }} />
          <span className="gap-stat-value" style={{ color: 'var(--zt-gap)' }}>{criticalGaps}</span>
          <span className="gap-stat-label">Critical (AB 869)</span>
        </div>
      </div>

      {/* Gaps by pillar */}
      {gapsByPillar.length > 0 && (
        <div className="gap-by-pillar">
          <h4 className="gap-section-title">
            <TrendingUp size={14} />
            Gaps by Pillar
          </h4>
          {gapsByPillar.map((pillarGap, idx) => {
            const color = PILLAR_COLORS[pillarGap.pillar_code] || 'var(--color-text-muted)';
            const total = pillarGap.total_gaps || pillarGap.gapCount || 0;
            const critical = pillarGap.critical_gaps || pillarGap.criticalCount || 0;

            return (
              <div key={pillarGap.pillar_code || idx} className="gap-pillar-row">
                <div className="gap-pillar-info">
                  <div
                    className="gap-pillar-dot"
                    style={{ backgroundColor: color }}
                  />
                  <span className="gap-pillar-name">{pillarGap.pillar_name || pillarGap.pillar_code}</span>
                </div>
                <div className="gap-pillar-counts">
                  {critical > 0 && (
                    <span className="gap-pillar-critical">{critical} critical</span>
                  )}
                  <span className="gap-pillar-total">{total} gap{total !== 1 ? 's' : ''}</span>
                </div>
                <div className="gap-pillar-bar-track">
                  <div
                    className="gap-pillar-bar-fill"
                    style={{
                      width: `${Math.min(100, (total / Math.max(totalGaps, 1)) * 100)}%`,
                      backgroundColor: color
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Individual gap items */}
      {gapsByPillar.some(pg => (pg.gaps || pg.capabilities || []).length > 0) && (
        <div className="gap-items">
          <h4 className="gap-section-title">Gap Details</h4>
          {gapsByPillar.map(pillarGap => {
            const items = pillarGap.gaps || pillarGap.capabilities || [];
            if (!items.length) return null;
            const color = PILLAR_COLORS[pillarGap.pillar_code] || 'var(--color-text-muted)';

            return (
              <div key={pillarGap.pillar_code} className="gap-pillar-section">
                <div className="gap-pillar-section-header" style={{ color }}>
                  {pillarGap.pillar_name || pillarGap.pillar_code}
                </div>
                {items.map((item, idx) => {
                  const priority = item.priority || 'MEDIUM';
                  const conf = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.MEDIUM;
                  const Icon = conf.icon;

                  return (
                    <div key={item.zt_capability_id || idx} className="gap-item">
                      <Icon size={13} style={{ color: conf.color, flexShrink: 0 }} />
                      <div className="gap-item-content">
                        <span className="gap-item-name">{item.capability_name || item.name}</span>
                        {item.current_maturity && (
                          <span className="gap-item-current">
                            Current: {item.current_maturity}
                          </span>
                        )}
                        {item.target_maturity && (
                          <span className="gap-item-target">
                            Target: {item.target_maturity}
                          </span>
                        )}
                      </div>
                      <span
                        className="gap-item-priority"
                        style={{ color: conf.color }}
                      >
                        {conf.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GapAnalysis;
